import React, { useEffect, useState, useMemo } from 'react';
import { digitalApi as api } from './api';
import { C, ACCENT } from '../../theme';
import DigitalPlanningEngine from './DigitalPlanningEngine';

const STATUS_ORDER = ['draft', 'active', 'approved', 'completed'];
const STATUS_LABEL = { draft:'Draft', active:'Active', approved:'Approved', completed:'Completed' };
const STATUS_VARIANT = { draft:'neutral', active:'info', approved:'success', completed:'neutral' };

export default function AccountOverviewPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState(new Set(['draft','active','approved','completed']));
  const [excludedBrands, setExcludedBrands] = useState(new Set());

  useEffect(() => {
    api.getPlansOverview()
      .then(setPlans)
      .catch(e => setError(e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const allBrands = useMemo(() => [...new Set(plans.map(p => p.brand_name))].sort(), [plans]);

  function toggleStatus(s) {
    setStatusFilter(prev => {
      const next = new Set(prev);
      next.has(s) ? next.delete(s) : next.add(s);
      return next;
    });
  }

  function toggleBrand(b) {
    setExcludedBrands(prev => {
      const next = new Set(prev);
      next.has(b) ? next.delete(b) : next.add(b);
      return next;
    });
  }

  const activePlans = useMemo(() =>
    plans.filter(p => statusFilter.has(p.status) && !excludedBrands.has(p.brand_name)),
    [plans, statusFilter, excludedBrands]
  );

  const engineData = useMemo(() => {
    const allFlights = [];
    const audienceMap = {};
    const actualsData = {};

    for (const plan of activePlans) {
      const d = plan.data || {};
      const flights = d.flights || [];
      for (const fl of flights) {
        allFlights.push({ ...fl, brand: plan.brand_name });
      }
      for (const aud of (d.audiences || [])) {
        audienceMap[aud.id] = aud;
      }
      Object.assign(actualsData, d.actualsData || {});
    }

    return {
      flights: allFlights,
      audiences: Object.values(audienceMap),
      actualsData,
    };
  }, [activePlans]);

  if (loading) return <div style={{ ...C.page, color: '#8aa0b8' }}>Loading overview…</div>;
  if (error) return <div style={{ ...C.page, color: '#a02020' }}>{error}</div>;

  const hasFlights = engineData.flights.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden', background: 'linear-gradient(135deg,#e8eef5,#d8e8f2)', fontFamily: "'Inter',sans-serif" }}>

      {/* Header bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 20px', background: 'rgba(255,255,255,0.9)', borderBottom: '1px solid #dce8f0', flexShrink: 0, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 800, fontSize: 15, color: '#1a2a3a' }}>Account Overview</span>
        <span style={{ color: '#8aa0b8', fontSize: 12 }}>{activePlans.length} plan{activePlans.length !== 1 ? 's' : ''} · {engineData.flights.length} flight{engineData.flights.length !== 1 ? 's' : ''}</span>

        <div style={{ width: 1, height: 20, background: '#dce8f0', margin: '0 4px' }}/>

        {/* Status filter */}
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: '#8aa0b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}>Status</span>
          {STATUS_ORDER.map(s => (
            <button key={s} onClick={() => toggleStatus(s)} style={{
              padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: '1px solid',
              background: statusFilter.has(s) ? `${ACCENT}15` : 'transparent',
              color: statusFilter.has(s) ? ACCENT : '#8aa0b8',
              borderColor: statusFilter.has(s) ? `${ACCENT}50` : '#dce8f0',
            }}>{STATUS_LABEL[s]}</button>
          ))}
        </div>

        {allBrands.length > 1 && <>
          <div style={{ width: 1, height: 20, background: '#dce8f0', margin: '0 4px' }}/>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 10, color: '#8aa0b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}>Brands</span>
            {allBrands.map(b => (
              <button key={b} onClick={() => toggleBrand(b)} style={{
                padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: '1px solid',
                background: !excludedBrands.has(b) ? '#1a2a3a15' : 'transparent',
                color: !excludedBrands.has(b) ? '#1a2a3a' : '#8aa0b8',
                borderColor: !excludedBrands.has(b) ? '#1a2a3a40' : '#dce8f0',
              }}>{b}</button>
            ))}
          </div>
        </>}
      </div>

      {/* Engine or empty state */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {hasFlights ? (
          <DigitalPlanningEngine
            key={JSON.stringify(engineData.flights.map(f => f.id))}
            initialData={engineData}
            readOnly
            aggregateMode
          />
        ) : (
          <div style={{ ...C.page, color: '#8aa0b8', textAlign: 'center', paddingTop: 80 }}>
            No flights found for the selected filters.
          </div>
        )}
      </div>
    </div>
  );
}
